import React, { useState, useEffect, useCallback } from 'react';
import { UniversalHeader } from '../../../shared/ui';
import { habiApi, HabiHabit, CreateHabiHabitRequest } from '../../../api/habiApi';
import CreateHabiHabitModal from './CreateHabiHabitModal';
import styles from './HabiBotPage.module.css';

const formatDate = (d: Date) => d.toISOString().slice(0, 10);
const formatDisplayDate = (s: string) => {
  const d = new Date(s + 'T12:00:00');
  return d.toLocaleDateString('ru-RU', { weekday: 'long', day: 'numeric', month: 'long' });
};

interface HabiBotPageProps {
  hideHeader?: boolean;
  onBack?: () => void;
  /** Встроен в дашборд — без своей шапки, модал управляется снаружи */
  embedded?: boolean;
  showCreateModal?: boolean;
  onOpenCreateModal?: () => void;
  onCloseCreateModal?: () => void;
  onHabitCreated?: () => void;
}

const HabiBotPage: React.FC<HabiBotPageProps> = ({
  hideHeader = false,
  onBack,
  embedded = false,
  showCreateModal: externalShowCreateModal = false,
  onOpenCreateModal,
  onCloseCreateModal,
  onHabitCreated
}) => {
  const [habits, setHabits] = useState<HabiHabit[]>([]);
  const [selectedDate, setSelectedDate] = useState<string>(formatDate(new Date()));
  const [loading, setLoading] = useState(true);
  const [internalShowCreateModal, setInternalShowCreateModal] = useState(false);
  const [togglingId, setTogglingId] = useState<number | null>(null);

  const showCreateModal = embedded ? externalShowCreateModal : internalShowCreateModal;

  const loadHabits = useCallback(async (date?: string) => {
    try {
      setLoading(true);
      const res = await habiApi.getHabitsForDate(date || selectedDate);
      setHabits(res.habits);
      if (date) setSelectedDate(res.date);
    } catch (err) {
      console.error('HabiBot load error:', err);
      setHabits([]);
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => {
    loadHabits(selectedDate);
  }, [selectedDate, loadHabits]);

  const handleToggle = async (habit: HabiHabit) => {
    if (togglingId !== null) return;
    setTogglingId(habit.id);
    try {
      const res = await habiApi.toggleCompletion(habit.id, selectedDate);
      setHabits(prev =>
        prev.map(h =>
          h.id === habit.id ? { ...h, isCompleted: res.isCompleted } : h
        )
      );
    } catch (err) {
      console.error('Toggle error:', err);
    } finally {
      setTogglingId(null);
    }
  };

  const handleCreate = async (req: CreateHabiHabitRequest) => {
    await habiApi.createHabit(req);
    if (embedded) {
      onHabitCreated?.();
    } else {
      setInternalShowCreateModal(false);
    }
    loadHabits();
  };

  const handleDelete = async (habitId: number) => {
    if (!window.confirm('Удалить привычку?')) return;
    try {
      await habiApi.deleteHabit(habitId);
      loadHabits();
    } catch (err) {
      console.error('Delete error:', err);
    }
  };

  const goPrevDay = () => {
    const d = new Date(selectedDate + 'T12:00:00');
    d.setDate(d.getDate() - 1);
    setSelectedDate(formatDate(d));
  };

  const goNextDay = () => {
    const d = new Date(selectedDate + 'T12:00:00');
    d.setDate(d.getDate() + 1);
    setSelectedDate(formatDate(d));
  };

  const goToday = () => {
    setSelectedDate(formatDate(new Date()));
  };

  const isToday = selectedDate === formatDate(new Date());

  return (
    <div className={styles.page}>
      {!embedded && (onBack && hideHeader) ? (
        <div className={styles.headerBar}>
          <button type="button" className={styles.backBtn} onClick={onBack} aria-label="Назад">
            <i className="ri-arrow-left-s-line" />
          </button>
          <h1 className={styles.headerTitle}>Ryaskov.pro</h1>
          <button
            type="button"
            className={styles.addBtn}
            onClick={() => setInternalShowCreateModal(true)}
            aria-label="Добавить дело"
          >
            <i className="ri-add-line" />
          </button>
        </div>
      ) : !embedded && !hideHeader ? (
        <UniversalHeader
          title="HabiBot"
          showBackButton={!!onBack}
          onBack={onBack}
          headerActions={
            <button
              type="button"
              className={styles.addBtn}
              onClick={() => setInternalShowCreateModal(true)}
              aria-label="Добавить дело"
            >
              <i className="ri-add-line" />
            </button>
          }
        />
      ) : null}

      <div className={styles.content}>
        <div className={styles.dateNav}>
          <button type="button" onClick={goPrevDay} className={styles.navBtn} aria-label="Предыдущий день">
            <i className="ri-arrow-left-s-line" />
          </button>
          <button
            type="button"
            className={`${styles.dateLabel} ${isToday ? styles.today : ''}`}
            onClick={goToday}
            title="Сегодня"
          >
            {formatDisplayDate(selectedDate)}
          </button>
          <button type="button" onClick={goNextDay} className={styles.navBtn} aria-label="Следующий день">
            <i className="ri-arrow-right-s-line" />
          </button>
        </div>

        {loading ? (
          <div className={styles.loading}>
            <i className="ri-loader-4-line habi-spin" />
            <span>Загрузка...</span>
          </div>
        ) : habits.length === 0 ? (
          <div className={styles.empty}>
            <div className={styles.emptyIcon}>
              <i className="ri-checkbox-blank-circle-line" />
            </div>
            <p className={styles.emptyTitle}>Нет дел на этот день</p>
            <p className={styles.emptyHint}>
              Добавьте ежедневную привычку или одноразовое дело
            </p>
            <button
              type="button"
              className={styles.addFirstBtn}
              onClick={() => (embedded ? onOpenCreateModal?.() : setInternalShowCreateModal(true))}
            >
              <i className="ri-add-line" /> Добавить
            </button>
          </div>
        ) : (
          <ul className={styles.list}>
            {habits.map((h) => (
              <li key={h.id} className={styles.item}>
                <button
                  type="button"
                  className={`${styles.checkbox} ${h.isCompleted ? styles.checked : ''}`}
                  onClick={() => handleToggle(h)}
                  disabled={togglingId === h.id}
                  aria-label={h.isCompleted ? 'Снять галочку' : 'Отметить выполненным'}
                >
                  {h.isCompleted ? (
                    <i className="ri-checkbox-circle-fill" />
                  ) : (
                    <i className="ri-checkbox-blank-circle-line" />
                  )}
                </button>
                <span className={`${styles.title} ${h.isCompleted ? styles.done : ''}`}>
                  {h.title}
                </span>
                {!h.isDaily && h.scheduledDate && (
                  <span className={styles.oneTime}>одноразовое</span>
                )}
                <button
                  type="button"
                  className={styles.deleteBtn}
                  onClick={() => handleDelete(h.id)}
                  aria-label="Удалить"
                >
                  <i className="ri-delete-bin-line" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {showCreateModal && (
        <CreateHabiHabitModal
          selectedDate={selectedDate}
          onClose={() => (embedded ? onCloseCreateModal?.() : setInternalShowCreateModal(false))}
          onCreate={handleCreate}
        />
      )}
    </div>
  );
};

export default HabiBotPage;
